import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@mui/material/styles';

import { useStyles } from './AppTimeWheelPicker.styles';

const ampmItems = [
  { value: 'AM', label: 'AM' },
  { value: 'PM', label: 'PM' },
];

function buildHourItems(hoursFormat: number, maxHours: number) {
  const length = hoursFormat === 24 ? maxHours + 1 : 12;
  return Array.from({ length }, (_, index) => ({
    value: hoursFormat === 24 ? index : index + 1,
    label: `${(hoursFormat === 24 ? index : index + 1)
      .toString()
      .padStart(2, '0')}`,
  }));
}

function buildMinuteItems(maxMinutes: number, step = 1) {
  const count = Math.floor(maxMinutes / step) + 1;
  return Array.from({ length: count }, (_, index) => {
    const val = index * step;
    return {
      value: val,
      label: `${val.toString().padStart(2, '0')}`,
    };
  });
}

function buildSecondItems(maxSeconds: number, step = 1) {
  const count = Math.floor(maxSeconds / step) + 1;
  return Array.from({ length: count }, (_, index) => {
    const val = index * step;
    return {
      value: val,
      label: `${val.toString().padStart(2, '0')}`,
    };
  });
}

interface TimeWheelPickerProps {
  onChange: (value: {
    hour?: number;
    minute?: number;
    second?: number;
    ampm?: string;
  }) => void;
  options?: {
    containerHeight?: number;
    itemHeight?: number;
    hoursFormat?: number;
    showHours?: boolean;
    showMinutes?: boolean;
    showSeconds?: boolean;
    maxHours?: number;
    maxMinutes?: number;
    maxSeconds?: number;
    minuteStep?: number;
    secondStep?: number;
  };
  value: {
    hour?: number;
    minute?: number;
    second?: number;
    ampm?: string;
  };
}

function TimeWheelPickerComponent({
  onChange = (v) => {},
  options = {},
  value = {},
}: TimeWheelPickerProps) {
  const {
    containerHeight = 210,
    itemHeight = 32,
    hoursFormat = 24,
    showHours = false,
    showMinutes = true,
    showSeconds = true,
    maxHours = 24,
    maxMinutes = 59,
    maxSeconds = 59,
    minuteStep = 1,
    secondStep = 1,
  } = options;
  const theme = useTheme();
  const { t } = useTranslation();

  const { classes } = useStyles(theme);

  const {
    hour: hourValue,
    minute: minuteValue,
    second: secondValue,
    ampm: ampmValue,
  } = value;

  const hourItems = useMemo(
    () => buildHourItems(hoursFormat, maxHours),
    [hoursFormat, maxHours],
  );
  const minuteItems = useMemo(
    () => buildMinuteItems(maxMinutes, minuteStep),
    [maxMinutes, minuteStep],
  );
  const secondItems = useMemo(
    () => buildSecondItems(maxSeconds, secondStep),
    [maxSeconds, secondStep],
  );

  const hourItemsContRef = useRef<HTMLUListElement>(null);
  const minuteItemsContRef = useRef<HTMLUListElement>(null);
  const secondItemsContRef = useRef<HTMLUListElement>(null);
  const ampmItemsContRef = useRef<HTMLUListElement>(null);
  const isScrolling = useRef(false);
  const hourRefs = useRef<(HTMLLIElement | null)[]>([]);
  const minuteRefs = useRef<(HTMLLIElement | null)[]>([]);
  const secondRefs = useRef<(HTMLLIElement | null)[]>([]);
  const ampmRefs = useRef<(HTMLLIElement | null)[]>([]);

  const hourItemsMap = hourItems.reduce(
    (map, item, index) => map.set(item.value, index),
    new Map(),
  );
  const currentHourValue = useRef(hourItemsMap.get(hourValue) ?? 0);

  const minuteItemsMap = minuteItems.reduce(
    (map, item, index) => map.set(item.value, index),
    new Map(),
  );
  const currentMinuteValue = useRef(minuteItemsMap.get(minuteValue) ?? 0);

  const secondItemsMap = secondItems.reduce(
    (map, item, index) => map.set(item.value, index),
    new Map(),
  );
  const currentSecondValue = useRef(secondItemsMap.get(secondValue) ?? 0);

  const ampmItemsMap = ampmItems.reduce(
    (map, item, index) => map.set(item.value, index),
    new Map(),
  );
  const currentAmpmValue = useRef(ampmItemsMap.get(ampmValue) ?? 0);

  const visibleItemsCount = Math.floor(containerHeight / itemHeight);
  const offset = Math.round((visibleItemsCount + 1) / 2) + 1;

  const getWheelMetrics = (
    container: HTMLElement | null,
    refs: React.RefObject<(HTMLLIElement | null)[]>,
  ) => {
    const viewportHeight = container?.clientHeight ?? containerHeight;
    const firstItem = refs.current[0];
    const firstItemCenter = firstItem
      ? firstItem.offsetTop + itemHeight / 2
      : itemHeight / 2;
    const maxScrollOffset = Math.max((viewportHeight - itemHeight) / 2, 1);
    return { viewportHeight, firstItemCenter, maxScrollOffset };
  };

  function rerenderElements(
    selectedElement: number,
    scrollTop: number,
    items: Map<number, number>,
    ref: React.RefObject<(HTMLLIElement | null)[]>,
    viewportHeight: number,
    firstItemCenter: number,
    maxScrollOffset: number,
  ) {
    const firstItemIndex = Math.max(selectedElement - offset, 0);
    const lastItemIndex = Math.min(selectedElement + offset, items.size);
    const viewportCenter = scrollTop + viewportHeight / 2;

    if (ref.current) {
      ref.current
        .slice(firstItemIndex, lastItemIndex)
        .forEach((item, index) => {
          const realIndex = index + firstItemIndex;
          const itemCenter = firstItemCenter + realIndex * itemHeight;
          const scrollOffset = Math.min(
            Math.abs(viewportCenter - itemCenter),
            maxScrollOffset,
          );
          const sin = scrollOffset / maxScrollOffset;
          const cos = Math.sqrt(1 - sin ** 2);
          const [div] = item.getElementsByTagName('div');
          div.style.transform = `rotateX(${Math.asin(sin)}rad) scale(${cos})`;
          div.style.transformOrigin = 'left';
        });
    }
  }

  const getHandleScroll = (
    itemsMap: Map<number, number>,
    refs: React.RefObject<(HTMLLIElement | null)[]>,
    items: Array<{ value: number | string; label: string }>,
    currentValue: React.MutableRefObject<number>,
    handleChange: (value: number) => void,
  ) => {
    let isAnimating = false;

    return (event: Event) => {
      if (!isAnimating) {
        isAnimating = true;

        requestAnimationFrame(() => {
          const target = event.target as HTMLElement;
          const scrollTop = Math.max(target.scrollTop, 0);
          const { viewportHeight, firstItemCenter, maxScrollOffset } =
            getWheelMetrics(target, refs);
          const selectedRaw =
            (scrollTop + viewportHeight / 2 - firstItemCenter) / itemHeight;
          const selectedElement = Math.min(
            Math.max(Math.round(selectedRaw), 0),
            items.length - 1,
          );
          //@ts-ignore
          window.clearTimeout(isScrolling.current);
          //@ts-ignore
          rerenderElements(
            selectedElement,
            scrollTop,
            itemsMap,
            refs,
            viewportHeight,
            firstItemCenter,
            maxScrollOffset,
          );

          currentValue.current = selectedElement;
          //@ts-ignore
          isScrolling.current = setTimeout(() => {
            handleChange(items[selectedElement].value as any);
          }, 20);

          isAnimating = false;
        });
      }
    };
  };

  useEffect(() => {
    const handleHourChange = (value: number) => onChange({ hour: value });

    const handleHourScroll = getHandleScroll(
      hourItemsMap,
      hourRefs,
      hourItems,
      currentHourValue,
      handleHourChange,
    );

    hourItemsContRef.current?.addEventListener('scroll', handleHourScroll);
    hourRefs.current[currentHourValue.current]?.scrollIntoView({
      block: 'center',
    });
    const hourMetrics = getWheelMetrics(hourItemsContRef.current, hourRefs);
    //@ts-ignore
    rerenderElements(
      currentHourValue.current,
      hourItemsContRef.current?.scrollTop || 0,
      hourItemsMap,
      hourRefs,
      hourMetrics.viewportHeight,
      hourMetrics.firstItemCenter,
      hourMetrics.maxScrollOffset,
    );

    return () => {
      hourItemsContRef.current?.removeEventListener('scroll', handleHourScroll);
    };
  }, [hourItemsContRef.current]);

  useEffect(() => {
    const handleMinuteChange = (value: number) => onChange({ minute: value });

    const handleMinuteScroll = getHandleScroll(
      minuteItemsMap,
      minuteRefs,
      minuteItems,
      currentMinuteValue,
      handleMinuteChange,
    );

    minuteItemsContRef.current?.addEventListener('scroll', handleMinuteScroll);
    minuteRefs.current[currentMinuteValue.current]?.scrollIntoView({
      block: 'center',
    });
    const minuteMetrics = getWheelMetrics(
      minuteItemsContRef.current,
      minuteRefs,
    );
    //@ts-ignore
    rerenderElements(
      currentMinuteValue.current,
      minuteItemsContRef.current?.scrollTop || 0,
      minuteItemsMap,
      minuteRefs,
      minuteMetrics.viewportHeight,
      minuteMetrics.firstItemCenter,
      minuteMetrics.maxScrollOffset,
    );

    return () => {
      minuteItemsContRef.current?.removeEventListener(
        'scroll',
        handleMinuteScroll,
      );
    };
  }, [minuteItemsContRef.current]);

  useEffect(() => {
    const handleSecondChange = (value: number) => onChange({ second: value });

    const handleSecondScroll = getHandleScroll(
      secondItemsMap,
      secondRefs,
      secondItems,
      currentSecondValue,
      handleSecondChange,
    );

    secondItemsContRef.current?.addEventListener('scroll', handleSecondScroll);
    secondRefs.current[currentSecondValue.current]?.scrollIntoView({
      block: 'center',
    });
    const secondMetrics = getWheelMetrics(
      secondItemsContRef.current,
      secondRefs,
    );
    //@ts-ignore
    rerenderElements(
      currentSecondValue.current,
      secondItemsContRef.current?.scrollTop || 0,
      secondItemsMap,
      secondRefs,
      secondMetrics.viewportHeight,
      secondMetrics.firstItemCenter,
      secondMetrics.maxScrollOffset,
    );

    return () => {
      //@ts-ignore
      secondItemsContRef.current?.removeEventListener(
        'scroll',
        handleSecondScroll,
      );
    };
  }, [secondItemsContRef.current]);

  useEffect(() => {
    const handleAmpmChange = (value: string) => onChange({ ampm: value });

    //@ts-ignore
    const handleAmpmScroll = getHandleScroll(
      ampmItemsMap,
      ampmRefs,
      ampmItems,
      currentAmpmValue,
      handleAmpmChange as any,
    );

    ampmItemsContRef.current?.addEventListener('scroll', handleAmpmScroll);
    ampmRefs.current[currentAmpmValue.current]?.scrollIntoView({
      block: 'center',
    });
    const ampmMetrics = getWheelMetrics(ampmItemsContRef.current, ampmRefs);
    //@ts-ignore
    rerenderElements(
      currentAmpmValue.current,
      ampmItemsContRef.current?.scrollTop || 0,
      ampmItemsMap,
      ampmRefs,
      ampmMetrics.viewportHeight,
      ampmMetrics.firstItemCenter,
      ampmMetrics.maxScrollOffset,
    );

    return () => {
      //@ts-ignore
      ampmItemsContRef.current?.removeEventListener('scroll', handleAmpmScroll);
    };
  }, [ampmItemsContRef.current]);

  useEffect(() => {
    const index = hourItemsMap.get(hourValue);
    if (index !== currentHourValue.current) {
      currentHourValue.current = index;
      hourRefs.current[index]?.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
      const hourMetrics = getWheelMetrics(hourItemsContRef.current, hourRefs);
      //@ts-ignore
      rerenderElements(
        currentHourValue.current,
        hourItemsContRef.current?.scrollTop || 0,
        hourItemsMap,
        hourRefs,
        hourMetrics.viewportHeight,
        hourMetrics.firstItemCenter,
        hourMetrics.maxScrollOffset,
      );
    }
  }, [hourValue]);

  useEffect(() => {
    const index = minuteItemsMap.get(minuteValue);
    if (index !== currentMinuteValue.current) {
      currentMinuteValue.current = index;
      minuteRefs.current[index]?.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
      const minuteMetrics = getWheelMetrics(
        minuteItemsContRef.current,
        minuteRefs,
      );
      //@ts-ignore
      rerenderElements(
        currentMinuteValue.current,
        minuteItemsContRef.current?.scrollTop || 0,
        minuteItemsMap,
        minuteRefs,
        minuteMetrics.viewportHeight,
        minuteMetrics.firstItemCenter,
        minuteMetrics.maxScrollOffset,
      );
    }
  }, [minuteValue]);

  useEffect(() => {
    const index = secondItemsMap.get(secondValue);
    if (index !== currentSecondValue.current) {
      currentSecondValue.current = index;
      secondRefs.current[index]?.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
      const secondMetrics = getWheelMetrics(
        secondItemsContRef.current,
        secondRefs,
      );
      //@ts-ignore
      rerenderElements(
        currentSecondValue.current,
        secondItemsContRef.current?.scrollTop || 0,
        secondItemsMap,
        secondRefs,
        secondMetrics.viewportHeight,
        secondMetrics.firstItemCenter,
        secondMetrics.maxScrollOffset,
      );
    }
  }, [secondValue]);

  useEffect(() => {
    const index = ampmItemsMap.get(ampmValue);
    if (index !== currentAmpmValue.current) {
      currentAmpmValue.current = index;
      ampmRefs.current[index]?.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
      const ampmMetrics = getWheelMetrics(ampmItemsContRef.current, ampmRefs);
      //@ts-ignore
      rerenderElements(
        currentAmpmValue.current,
        ampmItemsContRef.current?.scrollTop || 0,
        ampmItemsMap,
        ampmRefs,
        ampmMetrics.viewportHeight,
        ampmMetrics.firstItemCenter,
        ampmMetrics.maxScrollOffset,
      );
    }
  }, [ampmValue]);

  return (
    <div
      className={classes.wheelContainer}
      style={{
        height: `${containerHeight}px`,
        ['--wheel-item-height' as string]: `${itemHeight}px`,
      }}>
      {showHours && typeof hourValue === 'number' && (
        <div className={classes.wheelColumn}>
          <span className={classes.wheelLabel}>{t('common.hShort')}</span>
          <ul
            className={classes.wheelItems}
            ref={hourItemsContRef}
            style={{ padding: `${itemHeight * 3}px 0` }}>
            {hourItems.map((item, index) => (
              <li
                className={classes.wheelItem}
                key={item.value}
                ref={(node) => {
                  hourRefs.current[index] = node;
                }}
                style={{
                  height: `${itemHeight}px`,
                  lineHeight: `${itemHeight}px`,
                }}>
                <div
                  //@ts-ignore
                  onClick={(e) =>
                    e.target['scrollIntoView']({
                      behavior: 'smooth',
                      block: 'center',
                    })
                  }>
                  {item.label}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {showMinutes && (
        <div className={classes.wheelColumn}>
          <span className={classes.wheelLabel}>{t('common.minShort')}</span>
          <ul
            className={classes.wheelItems}
            ref={minuteItemsContRef}
            style={{ padding: `${itemHeight * 3}px 0` }}>
            {minuteItems.map((item, index) => (
              <li
                className={classes.wheelItem}
                key={item.value}
                ref={(node) => {
                  minuteRefs.current[index] = node;
                }}
                style={{
                  height: `${itemHeight}px`,
                  lineHeight: `${itemHeight}px`,
                }}>
                <div
                  //@ts-ignore
                  onClick={(e) =>
                    e.target['scrollIntoView']({
                      behavior: 'smooth',
                      block: 'center',
                    })
                  }>
                  {item.label}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {showSeconds && (
        <div className={classes.wheelColumn}>
          <span className={classes.wheelLabel}>{t('common.secShort')}</span>
          <ul
            className={classes.wheelItems}
            ref={secondItemsContRef}
            style={{ padding: `${itemHeight * 3}px 0` }}>
            {secondItems.map((item, index) => (
              <li
                className={classes.wheelItem}
                key={item.value}
                ref={(node) => {
                  secondRefs.current[index] = node;
                }}
                style={{
                  height: `${itemHeight}px`,
                  lineHeight: `${itemHeight}px`,
                }}>
                <div
                  //@ts-ignore
                  onClick={(e) =>
                    e.target['scrollIntoView']({
                      behavior: 'smooth',
                      block: 'center',
                    })
                  }>
                  {item.label}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {typeof hourValue === 'number' && hoursFormat === 12 && showHours && (
        <ul
          className={classes.wheelItems}
          ref={ampmItemsContRef}
          style={{ padding: `${itemHeight * 3}px 0` }}>
          {ampmItems.map((item, index) => (
            <li
              className={classes.wheelItem}
              key={item.value}
              ref={(node) => {
                ampmRefs.current[index] = node;
              }}
              style={{
                height: `${itemHeight}px`,
                lineHeight: `${itemHeight}px`,
              }}>
              <div
                //@ts-ignore
                onClick={(e) =>
                  e.target['scrollIntoView']({
                    behavior: 'smooth',
                    block: 'center',
                  })
                }>
                {item.label}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export const TimeWheelPicker = React.memo(TimeWheelPickerComponent);
